import { Injectable, RendererFactory2, DOCUMENT, inject } from '@angular/core';

import type { Renderer2 } from '@angular/core';

/**
 * This Service is used for loading scripts into the header of the index page.
 * Should be used only for loading external scripts such as analytics.
 */

export type ScriptLoaderAttributes = Partial<HTMLScriptElement>;

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private readonly document = inject<Document>(DOCUMENT);

  private scripts: Set<string> = new Set<string>();
  private renderer: Renderer2;

  constructor() {
    const rendererFactory = inject<RendererFactory2>(RendererFactory2);

    this.renderer = rendererFactory.createRenderer(null, null);
  }

  load(...scripts: Array<string>): Promise<unknown> {
    const promises: Array<unknown> = [];
    scripts.forEach((script: string): number =>
      promises.push(this.loadScript({ src: script }))
    );

    return Promise.all(promises);
  }

  loadScripts(...attrsList: Array<ScriptLoaderAttributes>): Promise<unknown> {
    const promises: Array<unknown> = [];
    attrsList.forEach((attrs: ScriptLoaderAttributes): number =>
      promises.push(this.loadScript(attrs))
    );

    return Promise.all(promises);
  }

  loadScript(attrs: ScriptLoaderAttributes): Promise<unknown> {
    return new Promise((resolve: (object: Record<string, unknown>) => void, reject: (object: Record<string, unknown>) => void): void => {
      const script = attrs.src?.toString() ?? attrs.innerText?.toString();
      if (script === undefined) {
        reject({ loaded: false, status: 'Missing script source' });
        return;
      }
      const scriptId = script;

      // resolve if already loaded
      if (this.scripts.has(scriptId)) {
        resolve({ script: scriptId, loaded: true, status: 'Already Loaded' });
      } else {
        // load script
        const element = this.renderer.createElement('script');
        element.type = 'text/javascript';
        this.loadAttributes(element, attrs);

        if (element.readyState) {  // IE
          element.onreadystatechange = (): void => {
            if (element.readyState === 'loaded' || element.readyState === 'complete') {
              element.onreadystatechange = null;
              this.scripts.add(scriptId);
              resolve({ script: scriptId, loaded: true, status: 'Loaded' });
            }
          };
        } else {  // Others
          element.onload = (): void => {
            this.scripts.add(scriptId);
            resolve({ script: scriptId, loaded: true, status: 'Loaded' });
          };
        }
        element.onerror = (error: unknown): void => reject({ script: scriptId, loaded: false, status: error });

        this.renderer.appendChild(this.document.head, element);
      }
    });
  }

  removeScript(script: string): void {
    const element = this.document.head.querySelector(`script[src="${script}"]`);
    if (element) {
      this.renderer.removeChild(this.document.head, element);
      this.scripts.delete(script);
    }
  }

  private loadAttributes(target: HTMLElement | DOMStringMap, attrs: ScriptLoaderAttributes | DOMStringMap): void {
    if (!target || typeof attrs !== 'object') {
      return;
    }
    const targetRecord = target as Record<string, unknown>;
    const attrsRecord = attrs as Record<string, unknown>;
    for (const name in attrsRecord) {
      if (name === 'dataset') {
        if ('dataset' in target) {
          const dataset = attrsRecord[name];
          if (dataset !== undefined) {
            this.loadAttributes(target.dataset as DOMStringMap, dataset);
          }
        }
      } else {
        targetRecord[name] = attrsRecord[name];
      }
    }
  }
}
